/**
 * SellHereTab — "Vendé acá" advisor.
 *
 * Para cada item del hangar dockeado:
 *  - Muestra best local sell / buy orders y cantidad de competition
 *  - Muestra tus existing orders (para no duplicar)
 *  - Muestra Jita sell como reference
 *  - Recomienda un precio (undercut 0.01, match, premium, haul, Jita ref)
 *
 * Auto-detecta location (NPC station vs player structure) — el backend hace
 * la distinción y elige el endpoint correcto (/markets/{region}/orders vs
 * /markets/structures/{id}/orders).
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'

type Verdict =
  | 'sell_here_undercut'
  | 'sell_here_match'
  | 'sell_here_premium'
  | 'haul_jita'
  | 'no_local_market'
  | 'insufficient_data'

type Advisory = {
  type_id: number
  name: string
  quantity: number
  best_local_sell: { price: number; volume: number } | null
  best_local_buy: { price: number; volume: number } | null
  local_sell_count: number
  local_buy_count: number
  your_existing_orders: number
  jita_sell: number | null
  recommended_price: number | null
  advice: { verdict: Verdict; reason: string; delta_vs_jita_pct: number | null }
}

const VERDICT_FILTERS: Array<{ key: Verdict | 'all'; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'sell_here_undercut', label: 'Undercut' },
  { key: 'sell_here_match', label: 'Ya listo' },
  { key: 'sell_here_premium', label: 'Premium' },
  { key: 'haul_jita', label: 'Haul a Jita' },
  { key: 'no_local_market', label: 'Sin mercado' },
]

function verdictClasses(v: Verdict): string {
  switch (v) {
    case 'sell_here_undercut':
      return 'bg-eve-success/10 border-eve-success text-eve-success'
    case 'sell_here_match':
      return 'bg-eve-muted/10 border-eve-border text-eve-muted'
    case 'sell_here_premium':
      return 'bg-eve-accent/10 border-eve-accent text-eve-accent'
    case 'haul_jita':
      return 'bg-eve-danger/10 border-eve-danger text-eve-danger'
    case 'no_local_market':
      return 'bg-eve-muted/10 border-eve-border text-eve-muted'
    case 'insufficient_data':
      return 'bg-eve-muted/10 border-eve-border text-eve-muted'
  }
}

function verdictLabel(v: Verdict): string {
  switch (v) {
    case 'sell_here_undercut':
      return 'Undercut'
    case 'sell_here_match':
      return 'Ya listo'
    case 'sell_here_premium':
      return 'Premium'
    case 'haul_jita':
      return 'Haul Jita'
    case 'no_local_market':
      return 'Sin mercado'
    case 'insufficient_data':
      return 'Sin datos'
  }
}

export function SellHereTab() {
  const { key: forceRefetchKey, force, bump } = useForceRefetch()
  const [verdictFilter, setVerdictFilter] = useState<Verdict | 'all'>('all')
  const [search, setSearch] = useState('')

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['utility-sell-here', forceRefetchKey],
    queryFn: () => api.utilitySellHere(force),
  })

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (data?.items ?? []).filter((it) => {
      if (verdictFilter !== 'all' && it.advice.verdict !== verdictFilter) return false
      if (q && !it.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [data, verdictFilter, search])

  const loc = data?.location
  const summary = data?.summary
  const locLabel =
    loc?.kind === 'in_space'
      ? 'In space'
      : loc?.kind === 'structure'
        ? `Structure ${loc.structure_id}`
        : loc?.kind === 'station'
          ? `Station ${loc.station_id}`
          : '—'

  return (
    <div className="space-y-4">
      <p className="text-eve-muted text-sm">
        "Vendé acá" — para cada item de tu hangar dockeado, miramos el order
        book local (NPC station o player structure) y te decimos a qué precio
        listarlo. Top 20 items por cantidad.
      </p>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryCard
            label="Location"
            value={locLabel}
            sub={
              loc?.region_id
                ? `Region ${loc.region_id}`
                : loc?.kind === 'in_space'
                  ? `System ${loc.solar_system_id ?? '—'}`
                  : '—'
            }
          />
          <SummaryCard
            label="Items analizados"
            value={String(summary?.total_items_analyzed ?? 0)}
            sub={
              summary && summary.total_items_skipped > 0
                ? `${summary.total_items_skipped} skipped (cap 20)`
                : 'todo el hangar'
            }
          />
          <SummaryCard
            label="Vendé acá"
            value={String(summary?.sell_here_wins ?? 0)}
            sub={`${summary?.no_local_market ?? 0} sin mercado · ${summary?.haul_wins ?? 0} haul`}
          />
          <SummaryCard
            label="Si seguís el advisor"
            value={`${formatISKShort(summary?.total_value_if_followed ?? 0)} ISK`}
            sub="revenue estimado"
            accent
          />
          <div className="card flex items-center justify-between">
            <div className="text-xs text-eve-muted">
              <LastUpdated timestamp={dataUpdatedAt} label="" />
            </div>
            <RefreshButton onClick={bump} isFetching={isFetching} />
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar item…"
            className="bg-eve-panel border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text placeholder:text-eve-muted focus:border-eve-accent focus:outline-none w-full md:w-64"
          />
          <select
            value={verdictFilter}
            onChange={(e) => setVerdictFilter(e.target.value as Verdict | 'all')}
            className="bg-eve-panel border border-eve-border rounded-md px-2 py-1.5 text-sm text-eve-text focus:border-eve-accent focus:outline-none"
          >
            {VERDICT_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
          <span className="text-eve-muted text-xs ml-auto">
            {filtered.length} / {data?.items.length ?? 0}
          </span>
        </div>

        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : loc?.kind === 'in_space' ? (
          <div className="text-eve-muted py-8 text-center italic">
            Estás en space — volve a dockear para leer el order book local.
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-eve-muted py-8 text-center">
            {data?.items.length === 0
              ? 'No tenés items en el hangar dockeado.'
              : 'Sin matches para el filtro actual.'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((it) => (
              <AdvisoryRow key={it.type_id} item={it} />
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-eve-muted">
        Para NPC stations el endpoint es region-wide ({'/markets/{region}/orders/'}),
        filtramos por location_id. Para player structures usamos
        {' '}{'/markets/structures/{id}/orders/'} que ya viene scoped. KV-cache
        5 min en ambos casos.
      </p>
    </div>
  )
}

function SummaryCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string
  value: string
  sub: React.ReactNode
  accent?: boolean
}) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${accent ? 'text-eve-accent' : ''}`}>
        {value}
      </div>
      <div className="text-xs text-eve-muted mt-1">{sub}</div>
    </div>
  )
}

function AdvisoryRow({ item }: { item: Advisory }) {
  const verdict = item.advice.verdict
  const recommendedTotal =
    item.recommended_price !== null ? item.recommended_price * item.quantity : null
  const localSellTotal =
    item.best_local_sell !== null ? item.best_local_sell.price * item.quantity : null

  return (
    <details className="border border-eve-border rounded-md bg-eve-bg/40">
      <summary className="cursor-pointer p-3 flex items-center gap-3 hover:bg-eve-panel/50 transition-colors">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-medium">{item.name}</span>
            <span className="text-xs text-eve-muted font-mono">
              × {item.quantity.toLocaleString('en-US')}
            </span>
            {item.your_existing_orders > 0 && (
              <span
                className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-eve-accent/10 border border-eve-accent/30 text-eve-accent"
                title={`Tenés ${item.your_existing_orders} order(s) activa(s) para este item`}
              >
                {item.your_existing_orders} ya listada{item.your_existing_orders === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="text-xs text-eve-muted mt-1">{item.advice.reason}</div>
        </div>
        <div className="flex items-center gap-3">
          <PricePill
            label="Recomendado"
            value={recommendedTotal}
            perUnit={item.recommended_price}
            accent
          />
          <PricePill label="Best local" value={localSellTotal} perUnit={item.best_local_sell?.price ?? null} />
          <span
            className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border whitespace-nowrap ${verdictClasses(verdict)}`}
          >
            {verdictLabel(verdict)}
          </span>
        </div>
      </summary>
      <div className="border-t border-eve-border px-3 py-3 text-xs space-y-1">
        <Row label="Best local sell" value={fmt(item.best_local_sell?.price ?? null)} />
        <Row
          label="Best local buy"
          value={fmt(item.best_local_buy?.price ?? null)}
        />
        <Row label="Jita sell (ref)" value={fmt(item.jita_sell)} />
        <Row label="Recomendado" value={fmt(item.recommended_price)} bold />
        <Row label="Local sell orders" value={String(item.local_sell_count)} />
        <Row label="Local buy orders" value={String(item.local_buy_count)} />
        <Row label="Tus orders activas" value={String(item.your_existing_orders)} />
        {item.advice.delta_vs_jita_pct !== null && (
          <Row
            label="Δ vs Jita (local)"
            value={`${item.advice.delta_vs_jita_pct >= 0 ? '+' : ''}${item.advice.delta_vs_jita_pct.toFixed(1)}%`}
          />
        )}
      </div>
    </details>
  )
}

function PricePill({
  label,
  value,
  perUnit,
  accent,
}: {
  label: string
  value: number | null
  perUnit: number | null
  accent?: boolean
}) {
  return (
    <div className="text-right">
      <div className="text-[10px] text-eve-muted uppercase font-mono">{label}</div>
      <div className={`text-sm font-mono ${accent ? 'text-eve-accent font-semibold' : ''}`}>
        {value !== null ? formatISKShort(value) : '—'}
      </div>
      {perUnit !== null && (
        <div className="text-[9px] text-eve-muted font-mono">
          {formatISK(perUnit, 2)}/u
        </div>
      )}
    </div>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between gap-2 ${bold ? 'font-semibold' : ''}`}>
      <span className="text-eve-muted">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

function fmt(n: number | null): string {
  return n !== null ? formatISK(n, 2) : '—'
}